import React, { useEffect, useRef } from 'react';

import { Spin as AntdSpin } from 'antd';

import {
  CreateUserRequestDtoProfileEnum,
  CreateViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import { Select, useFormActionsContext } from 'components/WrappedComponents';
import { Empty } from 'components/WrappedComponents/Empty';
import { WaitingReferentielLoadingField } from 'components/WrappedComponents/Form/FormFields/Select/Referentiels/WaitingReferentielLoadingField';
import { FieldProps } from 'components/WrappedComponents/Form/FormWrapper/type';
import { useApi } from 'hooks/ApiStoreContext';
import { useRefAutoriteFourriere } from 'hooks/referentiels/useRefAutoriteFourriere';
import { useRefFourriere } from 'hooks/referentiels/useRefFourriere';
import { useRefUniteFoSearch } from 'hooks/referentiels/useRefUniteFoSearch';
import { CreationCompteFormValues } from 'types/Compte/CreationCompte';
import { FormPlaceholders } from 'types/enums/FormPlaceholders';
import { OptionProps } from 'types/searchResult';
import { CSSUtils } from 'utils/CSSUtils';

import { createProfilesWithUniteRattachement } from '../../common';
import {
  autoriteFourriereToItem,
  fourriereToItem,
  uniteFoToItem,
} from '../../Modification/utils';
import {
  generateCreateUserRequestDtoFromFormData,
  validateCreateUserRequest,
} from '../utils';

import '../../style.less';

interface SelectUniteRattachement extends FieldProps<CreationCompteFormValues> {
  selectedProfile?: CreateUserRequestDtoProfileEnum;
  disabled?: boolean;
  PMtoSearch?: string;
}

const DROPDOWN_SELECTED_WIDTH = 350;

const SelectUniteRattachement = ({
  values,
  selectedProfile,
  disabled = false,
  PMtoSearch,
}: SelectUniteRattachement): React.ReactElement => {
  const [refUniteFo, searchUniteFo, isFetchingUniteFo, fetchUniteFo] =
    useRefUniteFoSearch(PMtoSearch);
  const refAutoriteFourriere = useRefAutoriteFourriere();
  const refFourriere = useRefFourriere();

  const formActions = useFormActionsContext();
  const valuesRef = useRef<CreationCompteFormValues>();
  valuesRef.current = values;

  const cuController = useApi().ComptesUtilisateursControllerApi;
  const key: keyof CreationCompteFormValues = 'uniteRattachement';

  useEffect(() => {
    if (
      !(
        values.profile &&
        createProfilesWithUniteRattachement.has(values.profile) &&
        !values.uniteRattachement
      )
    ) {
      void formActions.validateFields([key]);
    }
  }, [values.profile, formActions, key, values.uniteRattachement]);

  // content if no data found
  let notFoundContent = undefined;
  // list of options of select
  let selectItems: OptionProps[] = [];
  // action on search in select
  let onSearch = undefined;

  switch (selectedProfile) {
    case CreateUserRequestDtoProfileEnum.GARDIEN_FOURRIERE:
      selectItems =
        refFourriere !== undefined
          ? // Ref fetched
            refFourriere.referenceDtoList
              .sort((a, b) => a.raisonSociale.localeCompare(b.raisonSociale))
              .map(fourriereToItem)
          : [];
      break;
    case CreateUserRequestDtoProfileEnum.AUTORITE_FOURRIERE:
      selectItems =
        refAutoriteFourriere !== undefined
          ? // Ref fetched
            refAutoriteFourriere.referenceDtoList
              .sort((a, b) => a.libelleComplet.localeCompare(b.libelleComplet))
              .map(autoriteFourriereToItem)
          : [];
      break;
    case CreateUserRequestDtoProfileEnum.FORCE_DE_L_ORDRE:
      selectItems =
        refUniteFo !== undefined
          ? // Ref fetched
            refUniteFo.referenceDtoList
              .sort((a, b) => a.abreviation.localeCompare(b.abreviation))
              .map(uniteFoToItem)
          : [];
      notFoundContent = isFetchingUniteFo ? (
        <AntdSpin size="small" />
      ) : searchUniteFo !== undefined && searchUniteFo !== '' ? (
        <Empty />
      ) : null;
      onSearch = fetchUniteFo;
      break;
    default:
      break;
  }

  return refFourriere !== undefined && refAutoriteFourriere !== undefined ? (
    <Select
      wrapperProps={{
        values: values,
        fieldKey: key,
        itemProps: {
          label: 'Entité de rattachement',
          rules: [
            {
              required: true,
              validator: async (
                _rule,
                value: string | undefined,
              ): Promise<void> => {
                const requestObject = generateCreateUserRequestDtoFromFormData({
                  ...values,
                  uniteRattachement: value,
                });

                return await validateCreateUserRequest(
                  cuController,
                  requestObject,
                  CreateViolationDtoFormFieldEnum.ENTITE_RATTACHEMENT,
                );
              },
            },
          ],
          labelCol: { span: CSSUtils.defaultLabelCol },
          wrapperCol: { span: CSSUtils.largeWrapperCol },
          getValueProps: (value: unknown) => {
            if (typeof value === 'string') {
              return { value: value };
            }
            return {
              value: undefined,
            };
          },
        },
      }}
      optionProps={selectItems}
      selectProps={{
        disabled:
          disabled ||
          selectedProfile === undefined ||
          !createProfilesWithUniteRattachement.has(selectedProfile),
        placeholder: FormPlaceholders.Select,
        dropdownMatchSelectWidth: DROPDOWN_SELECTED_WIDTH,
        suffixIcon: null,
        onSearch: onSearch,
        notFoundContent: notFoundContent,
        allowClear: true,
      }}
    />
  ) : (
    <WaitingReferentielLoadingField title={'Entité de rattachement'} />
  );
};

export default SelectUniteRattachement;
